import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { usePolling } from "@/hooks/usePolling";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Storefront, Check, X, Clock, Percent, Receipt, CurrencyInr, TrendUp,
  ChartBar, Users, MagnifyingGlass, Funnel, Buildings, MapPin, CaretDown, CaretUp
} from "@phosphor-icons/react";
import { SEO } from "@/components/SEO";

const formatNumber = (num) => {
  if (num >= 10000000) return (num / 10000000).toFixed(1) + "Cr";
  if (num >= 100000) return (num / 100000).toFixed(1) + "L";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num?.toLocaleString("en-IN") || "0";
};

export default function AdminPartnerStoresPage() {
  const { token } = useAuth();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [cityFilter, setCityFilter] = useState("all");
  const [chapterFilter, setChapterFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [filterUsers, setFilterUsers] = useState([]);
  const [txnSearch, setTxnSearch] = useState("");
  const [expandedView, setExpandedView] = useState(null); // "stores" | "users" | null

  const fetchStores = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/partner-stores`, { headers: { Authorization: `Bearer ${token}` } });
      setStores(res.data);
    } catch {} finally { setLoading(false); }
  }, [token]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (cityFilter !== "all") params.append("city", cityFilter);
      if (chapterFilter !== "all") params.append("chapter_id", chapterFilter);
      if (userFilter !== "all") params.append("user_id", userFilter);
      const res = await axios.get(`${API}/admin/partner-store-analytics?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setAnalytics(res.data);
    } catch (e) { console.error("Analytics fetch failed:", e); }
    finally { setAnalyticsLoading(false); }
  }, [token, cityFilter, chapterFilter, userFilter]);

  const fetchFilterUsers = useCallback(async () => {
    try {
      const params = chapterFilter !== "all" ? `?chapter_id=${chapterFilter}` : "";
      const res = await axios.get(`${API}/admin/partner-store-analytics/users${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setFilterUsers(res.data);
    } catch {}
  }, [token, chapterFilter]);

  useEffect(() => { fetchStores(); }, [fetchStores]);
  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);
  useEffect(() => { fetchFilterUsers(); }, [fetchFilterUsers]);
  usePolling(fetchStores, 15000);
  usePolling(fetchAnalytics, 15000);

  const handleApprove = async (storeId, status) => {
    try {
      await axios.put(`${API}/admin/partner-stores/${storeId}/approve`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Store ${status}`);
      fetchStores();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const handleDiscountApproval = async (storeId, status) => {
    try {
      await axios.put(`${API}/admin/partner-stores/${storeId}/approve-discount`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Discount change ${status}`);
      fetchStores();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const storeFiltered = stores.filter(s => {
    if (tab === "pending") return s.status === "pending";
    if (tab === "approved") return s.status === "approved";
    if (tab === "discount") return s.discount_change_status === "pending";
    if (tab === "all_stores") return true;
    return false;
  });

  const filteredTxns = (analytics?.transactions || []).filter(t =>
    !txnSearch || t.member_name?.toLowerCase().includes(txnSearch.toLowerCase()) || t.store_name?.toLowerCase().includes(txnSearch.toLowerCase()) || t.member_phone?.includes(txnSearch)
  );

  if (loading) return <><div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" /></div></>;

  return (
    <>
    <div className="space-y-6" data-testid="admin-partner-stores">
      <SEO title="Partner Stores Management - BCH Admin" />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-primary text-2xl font-bold">Partner Stores</h1>
        <div className="flex gap-2 flex-wrap">
          {["analytics", "pending", "approved", "discount", "all_stores"].map(t => (
            <Button key={t} variant={tab === t ? "default" : "outline"} size="sm" onClick={() => setTab(t)}
              className={tab === t ? "bg-bch-teal text-white" : "border-[var(--border-color)] text-muted"}
              data-testid={`tab-${t}`}>
              {t === "analytics" ? "Analytics" : t === "discount" ? "Discount Requests" : t === "all_stores" ? "All" : t.charAt(0).toUpperCase() + t.slice(1)}
              {t === "pending" && stores.filter(s => s.status === "pending").length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 bg-amber-500 text-black text-[10px] rounded-full font-bold">{stores.filter(s => s.status === "pending").length}</span>
              )}
            </Button>
          ))}
        </div>
      </div>

      {/* Analytics Tab */}
      {tab === "analytics" && (
        <div className="space-y-5">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-end" data-testid="analytics-filters">
            <div className="min-w-[150px]">
              <label className="text-muted text-xs uppercase tracking-wider mb-1 block">City</label>
              <Select value={cityFilter} onValueChange={v => { setCityFilter(v); setChapterFilter("all"); setUserFilter("all"); }}>
                <SelectTrigger className="bg-surface border-[var(--border-color)] text-primary h-9" data-testid="filter-city">
                  <SelectValue placeholder="All Cities" />
                </SelectTrigger>
                <SelectContent className="bg-card border-[var(--border-color)]">
                  <SelectItem value="all">All Cities</SelectItem>
                  {(analytics?.filters?.cities || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[180px]">
              <label className="text-muted text-xs uppercase tracking-wider mb-1 block">Chapter</label>
              <Select value={chapterFilter} onValueChange={v => { setChapterFilter(v); setUserFilter("all"); }}>
                <SelectTrigger className="bg-surface border-[var(--border-color)] text-primary h-9" data-testid="filter-chapter">
                  <SelectValue placeholder="All Chapters" />
                </SelectTrigger>
                <SelectContent className="bg-card border-[var(--border-color)]">
                  <SelectItem value="all">All Chapters</SelectItem>
                  {(analytics?.filters?.chapters || [])
                    .filter(c => cityFilter === "all" || c.city === cityFilter)
                    .map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[180px]">
              <label className="text-muted text-xs uppercase tracking-wider mb-1 block">Member</label>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="bg-surface border-[var(--border-color)] text-primary h-9" data-testid="filter-user">
                  <SelectValue placeholder="All Members" />
                </SelectTrigger>
                <SelectContent className="bg-card border-[var(--border-color)]">
                  <SelectItem value="all">All Members</SelectItem>
                  {filterUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}{u.chapter_name ? ` (${u.chapter_name})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(cityFilter !== "all" || chapterFilter !== "all" || userFilter !== "all") && (
              <Button variant="ghost" size="sm" className="text-muted" onClick={() => { setCityFilter("all"); setChapterFilter("all"); setUserFilter("all"); }} data-testid="clear-filters">
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
            )}
          </div>

          {/* Overview Stats */}
          {analyticsLoading ? (
            <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" /></div>
          ) : analytics?.overview ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="analytics-overview">
                <Card className="bg-card border-[var(--border-color)]">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-bch-teal/10 flex items-center justify-center"><Receipt className="w-5 h-5 text-bch-teal" weight="duotone" /></div>
                      <div>
                        <p className="text-muted text-[10px] uppercase tracking-wider">Transactions</p>
                        <p className="text-primary text-xl font-bold" data-testid="overview-txns">{analytics.overview.total_transactions}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-card border-[var(--border-color)]">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center"><CurrencyInr className="w-5 h-5 text-blue-500" weight="duotone" /></div>
                      <div>
                        <p className="text-muted text-[10px] uppercase tracking-wider">Total Spent</p>
                        <p className="text-primary text-xl font-bold" data-testid="overview-spent">{formatNumber(analytics.overview.total_spent)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-card border-[var(--border-color)]">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center"><TrendUp className="w-5 h-5 text-emerald-500" weight="duotone" /></div>
                      <div>
                        <p className="text-muted text-[10px] uppercase tracking-wider">Total Saved</p>
                        <p className="text-primary text-xl font-bold text-emerald-500" data-testid="overview-saved">{formatNumber(analytics.overview.total_saved)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-card border-[var(--border-color)]">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-bch-gold/10 flex items-center justify-center"><CurrencyInr className="w-5 h-5 text-bch-gold" weight="duotone" /></div>
                      <div>
                        <p className="text-muted text-[10px] uppercase tracking-wider">Store Revenue</p>
                        <p className="text-primary text-xl font-bold" data-testid="overview-revenue">{formatNumber(analytics.overview.total_revenue)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-card border-[var(--border-color)]">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center"><Storefront className="w-5 h-5 text-violet-500" weight="duotone" /></div>
                      <div>
                        <p className="text-muted text-[10px] uppercase tracking-wider">Active Stores</p>
                        <p className="text-primary text-xl font-bold" data-testid="overview-stores">{analytics.overview.total_stores}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Store & User Breakdowns */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Per-Store */}
                <Card className="bg-card border-[var(--border-color)]">
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-primary text-sm flex items-center gap-2"><Storefront className="w-4 h-4 text-bch-gold" weight="duotone" /> Store Breakdown</CardTitle>
                    {analytics.store_breakdown?.length > 3 && (
                      <Button variant="ghost" size="sm" className="text-muted text-xs" onClick={() => setExpandedView(expandedView === "stores" ? null : "stores")}>
                        {expandedView === "stores" ? <><CaretUp className="w-3 h-3 mr-1" /> Less</> : <><CaretDown className="w-3 h-3 mr-1" /> All</>}
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      {(expandedView === "stores" ? analytics.store_breakdown : analytics.store_breakdown?.slice(0, 5))?.map(s => (
                        <div key={s.store_id} className="flex items-center justify-between p-3 bg-surface rounded-xl" data-testid={`store-stat-${s.store_id}`}>
                          <div>
                            <p className="text-primary text-sm font-medium">{s.store_name}</p>
                            <p className="text-muted text-xs">{s.transactions} txns</p>
                          </div>
                          <div className="text-right">
                            <p className="text-primary text-sm font-bold">{formatNumber(s.total_spent)}</p>
                            <p className="text-emerald-500 text-xs">Saved {formatNumber(s.total_saved)}</p>
                          </div>
                        </div>
                      ))}
                      {!analytics.store_breakdown?.length && <p className="text-muted text-sm text-center py-4">No store data</p>}
                    </div>
                  </CardContent>
                </Card>

                {/* Per-User */}
                <Card className="bg-card border-[var(--border-color)]">
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-primary text-sm flex items-center gap-2"><Users className="w-4 h-4 text-violet-500" weight="duotone" /> Member Usage</CardTitle>
                    {analytics.user_breakdown?.length > 3 && (
                      <Button variant="ghost" size="sm" className="text-muted text-xs" onClick={() => setExpandedView(expandedView === "users" ? null : "users")}>
                        {expandedView === "users" ? <><CaretUp className="w-3 h-3 mr-1" /> Less</> : <><CaretDown className="w-3 h-3 mr-1" /> All</>}
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      {(expandedView === "users" ? analytics.user_breakdown : analytics.user_breakdown?.slice(0, 5))?.map((u, i) => (
                        <div key={u.user_id || i} className="flex items-center justify-between p-3 bg-surface rounded-xl" data-testid={`user-stat-${u.user_id}`}>
                          <div>
                            <p className="text-primary text-sm font-medium">{u.member_name}</p>
                            <p className="text-muted text-xs">{u.chapter_name || u.member_phone} &middot; {u.transactions} txns</p>
                          </div>
                          <div className="text-right">
                            <p className="text-primary text-sm font-bold">{formatNumber(u.total_spent)}</p>
                            <p className="text-emerald-500 text-xs">Saved {formatNumber(u.total_saved)}</p>
                          </div>
                        </div>
                      ))}
                      {!analytics.user_breakdown?.length && <p className="text-muted text-sm text-center py-4">No user data</p>}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* All Transactions Table */}
              <Card className="bg-card border-[var(--border-color)]">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-primary text-sm flex items-center gap-2"><Receipt className="w-4 h-4 text-bch-teal" weight="duotone" /> All Transactions</CardTitle>
                  <div className="relative w-60">
                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                    <Input placeholder="Search name, store, phone..." value={txnSearch} onChange={e => setTxnSearch(e.target.value)}
                      className="pl-9 h-8 bg-surface border-[var(--border-color)] text-primary text-sm" data-testid="txn-search" />
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <ScrollArea className="h-[350px]">
                    {filteredTxns.length === 0 ? (
                      <div className="text-center py-8"><Receipt className="w-10 h-10 text-muted mx-auto mb-2" weight="duotone" /><p className="text-muted text-sm">No transactions found</p></div>
                    ) : (
                      <div className="space-y-2">
                        {/* Header */}
                        <div className="grid grid-cols-12 gap-2 px-3 py-2 text-muted text-[10px] uppercase tracking-wider font-semibold border-b border-[var(--border-color)]">
                          <div className="col-span-3">Member</div>
                          <div className="col-span-2">Store</div>
                          <div className="col-span-2">Chapter</div>
                          <div className="col-span-1 text-right">Bill</div>
                          <div className="col-span-1 text-right">Saved</div>
                          <div className="col-span-1 text-right">Paid</div>
                          <div className="col-span-2 text-right">Date</div>
                        </div>
                        {filteredTxns.map(txn => (
                          <div key={txn.id} className="grid grid-cols-12 gap-2 px-3 py-2.5 bg-surface rounded-lg items-center text-sm" data-testid={`txn-row-${txn.id}`}>
                            <div className="col-span-3">
                              <p className="text-primary font-medium truncate">{txn.member_name}</p>
                              <p className="text-muted text-xs">{txn.member_phone}</p>
                            </div>
                            <div className="col-span-2 text-primary truncate">{txn.store_name}</div>
                            <div className="col-span-2 text-muted text-xs truncate">{txn.chapter_name || "-"}</div>
                            <div className="col-span-1 text-right text-primary font-medium">{formatNumber(txn.total_bill)}</div>
                            <div className="col-span-1 text-right text-emerald-500 font-medium">{formatNumber(txn.discount_amount)}</div>
                            <div className="col-span-1 text-right text-primary font-bold">{formatNumber(txn.discounted_total)}</div>
                            <div className="col-span-2 text-right text-muted text-xs">{new Date(txn.created_at).toLocaleDateString()}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="bg-card border-[var(--border-color)]">
              <CardContent className="p-12 text-center">
                <ChartBar className="w-12 h-12 text-muted mx-auto mb-3" weight="duotone" />
                <p className="text-muted text-sm">No analytics data available</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Store Management Tabs */}
      {["pending", "approved", "discount", "all_stores"].includes(tab) && (
        <>
          {storeFiltered.length === 0 ? (
            <Card className="bg-card border-[var(--border-color)]">
              <CardContent className="p-12 text-center">
                <Storefront className="w-12 h-12 text-muted mx-auto mb-3" weight="duotone" />
                <p className="text-muted text-sm">No {tab === "all_stores" ? "" : tab} partner stores</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {storeFiltered.map(store => (
                <Card key={store.id} className="bg-card border-[var(--border-color)]" data-testid={`store-card-${store.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Storefront className="w-5 h-5 text-bch-gold" weight="duotone" />
                          <h3 className="text-primary font-semibold">{store.store_name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${store.status === "approved" ? "bg-green-500/10 text-green-500" : store.status === "pending" ? "bg-amber-500/10 text-amber-500" : "bg-red-500/10 text-red-500"}`}>{store.status}</span>
                          {!store.is_bch_member && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-blue-500/10 text-blue-500">External</span>}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div><p className="text-muted text-xs">Owner</p><p className="text-primary">{store.owner_name}</p></div>
                          <div><p className="text-muted text-xs">Category</p><p className="text-primary">{store.store_category}</p></div>
                          <div><p className="text-muted text-xs">Address</p><p className="text-primary truncate">{store.store_address}</p></div>
                          <div><p className="text-muted text-xs">Discount</p><p className="text-primary">{store.discount_percentage ? `${store.discount_percentage}%` : ""} {store.discount_fixed ? `Flat ${store.discount_fixed}` : ""}</p></div>
                        </div>
                        {store.status === "approved" && (
                          <div className="flex gap-4 mt-2 text-xs text-muted">
                            <span>{store.total_transactions || 0} transactions</span>
                            <span>Revenue: {formatNumber(store.total_revenue || 0)}</span>
                            <span>Discounts: {formatNumber(store.total_discount_given || 0)}</span>
                          </div>
                        )}
                        {store.discount_change_status === "pending" && (
                          <div className="mt-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                            <p className="text-amber-500 text-xs font-semibold flex items-center gap-1"><Percent className="w-3 h-3" /> Discount Change Requested</p>
                            <p className="text-muted text-xs mt-1">New: {store.pending_discount_percentage ? `${store.pending_discount_percentage}%` : ""} {store.pending_discount_fixed ? `Flat ${store.pending_discount_fixed}` : ""}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 ml-4 shrink-0">
                        {store.status === "pending" && (
                          <>
                            <Button onClick={() => handleApprove(store.id, "approved")} size="sm" className="bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-${store.id}`}>
                              <Check className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button onClick={() => handleApprove(store.id, "rejected")} size="sm" variant="outline" className="border-red-500/30 text-red-500 hover:bg-red-500/10" data-testid={`reject-${store.id}`}>
                              <X className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </>
                        )}
                        {store.discount_change_status === "pending" && (
                          <>
                            <Button onClick={() => handleDiscountApproval(store.id, "approved")} size="sm" className="bg-bch-gold hover:bg-bch-gold/90 text-black" data-testid={`approve-discount-${store.id}`}>
                              <Check className="w-4 h-4 mr-1" /> Approve Discount
                            </Button>
                            <Button onClick={() => handleDiscountApproval(store.id, "rejected")} size="sm" variant="outline" className="border-red-500/30 text-red-500 hover:bg-red-500/10">
                              <X className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
    </>
  );
}
